/**
 * Luxury tax on keepers.
 *
 * 2020-III-1 §2  — default allotment is 4 keepers; required farm players count inside it.
 * 2020-III-1 §4  — real money may NOT be used to buy keeper slots (this killed 2019-III-3's
 *                  "$10 real instead of $25 FAAB" option). FAAB only.
 * 2023           — maximum raised to 8 keepers; tax is $22 / $44 / $66 / $88 for keepers 5-8.
 * 2019-II-5 §2   — a farm player who fails to make an NFL roster STILL occupies a keeper slot
 *                  for tax purposes. The rulebook's own worked example: a GM declaring 5
 *                  keepers of whom 2 are failed farm players is "effectively keeping 3" but
 *                  is still charged the 5-keeper tax.
 * 2019-I-1       — a first-year GM gets amnesty: 6 keepers untaxed.
 * 2020-I-2 §1    — modified so a new GM pays only the tax owed for slots ABOVE 6.
 */

import type { LeagueConfig } from './config.js';

export interface KeeperSlate {
  teamId: string;
  /** Every player declared as a keeper, including farm graduates who failed to make a roster. */
  declared: Array<{
    playerId: string;
    /** True for a farm graduate who did not make an opening-day 53-man roster (2019-II-5). */
    farmForfeited?: boolean;
    /** Franchise-tagged: consumes three keeper slots instead of one (2025 P05 §2). */
    franchiseTagged?: boolean;
  }>;
  /** True in a GM's first season in the league (2019-I-1 amnesty). */
  firstYearGM?: boolean;
}

export interface TaxAssessment {
  teamId: string;
  /** Slots consumed, including forfeited farm slots. This is what the tax is priced on. */
  slotsUsed: number;
  /** Players who will actually be on the roster (excludes forfeited farm slots). */
  effectiveKeepers: number;
  /** FAAB owed. */
  taxFaab: number;
  /** Free slots this GM gets before tax starts. */
  freeSlots: number;
  legal: boolean;
  errors: string[];
  notes: string[];
}

export function assessLuxuryTax(slate: KeeperSlate, config: LeagueConfig): TaxAssessment {
  const errors: string[] = [];
  const notes: string[] = [];

  // 2025 P05 §2: a franchise-tagged player "will count as three (3) keeper slots for the
  // purposes of luxury tax and maximum number of keepers."
  const tagged = slate.declared.filter((d) => d.franchiseTagged);
  const slotsUsed = slate.declared.length + tagged.length * 2; // each tag adds 2 extra slots
  const forfeited = slate.declared.filter((d) => d.farmForfeited).length;
  const effectiveKeepers = slate.declared.length - forfeited;

  if (tagged.length > 1) {
    errors.push(
      `${tagged.length} franchise tags declared; a GM has only one per season (2025 P05 §1).`,
    );
  }
  if (tagged.length === 1) {
    notes.push(
      'Franchise tag consumes three of the eight slots (2025 P05 §2), leaving five for ' +
        'ordinary keepers. A franchise player is not a keeper — the two are separate ' +
        'designations — so a GM who tags retains six players in total: one franchise player ' +
        'plus five keepers. This is exactly what the rule\'s own example means by "limits his ' +
        'maximum number of keepers to 5". Confirmed by the commissioner, 2026.',
    );
  }

  if (forfeited > 0) {
    notes.push(
      `${forfeited} farm-system player(s) failed to make an NFL roster. Those slots are ` +
        `forfeited and cannot be filled with another player, but they still count toward the ` +
        `luxury tax (2019-II-5 §2).`,
    );
  }

  // 2019-I-1: a new GM may keep up to 6 with no tax. 2020-I-2 §1: above 6 he pays only the
  // increment for those extra slots — i.e. the tax he owes is the total for his slot count
  // minus the total for 6 slots.
  const freeSlots = slate.firstYearGM ? 6 : config.freeKeepers;
  if (slate.firstYearGM) {
    notes.push('First-year GM amnesty: 6 keepers with no luxury tax (2019-I-1, 2020-I-2 §1).');
  }

  if (slotsUsed > config.maxKeepers) {
    errors.push(
      `${slotsUsed} keepers declared; the maximum is ${config.maxKeepers} (2023 "Expand ` +
        `Maximum Number Of Eligible Keepers" §1).`,
    );
  }

  const table = config.luxuryTaxByKeeperCount;
  const totalFor = (n: number): number => table[Math.min(n, config.maxKeepers)] ?? 0;

  const taxFaab = slate.firstYearGM
    ? Math.max(0, totalFor(slotsUsed) - totalFor(freeSlots))
    : totalFor(slotsUsed);

  return {
    teamId: slate.teamId,
    slotsUsed,
    effectiveKeepers,
    taxFaab,
    freeSlots,
    legal: errors.length === 0,
    errors,
    notes,
  };
}
